import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Tilkøb af professionel montering.
 *
 * Rør IKKE hovedformularen. Horizons `handleSubmit` (product-form.js) er hårdkodet til
 * single-item `id`-format og kaster "Form ID is required" hvis feltet mangler — derfor
 * fejlede den tidligere bulk-injection. I stedet lytter vi på Horizons egen success-event
 * (`cart:update`, source `product-form-component`) og lægger montering i kurven med et
 * separat `/cart/add.js`-kald bagefter.
 */
class MonteringAddon extends HTMLElement {
  #adding = false;

  connectedCallback() {
    this.checkbox = this.querySelector('[ref="checkbox"]');
    // Scoping til at bekræfte at vi hører til en produktform (kun for tidlig exit).
    const scope = this.closest('.product-details') || this.closest('.product-information') || document;
    this.productForm =
      this.closest('product-form-component') || scope.querySelector('product-form-component');
    if (!this.checkbox || !this.productForm) return;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    // Kun hovedformens vellykkede tilføjelse — ikke vores egen montering-event, ikke fejl.
    if (this.#adding) return;
    if (data?.source !== 'product-form-component' || data.didError) return;
    // Erhverv får forhandlet montering (kontakt-tekst), aldrig fast-pris i kurv.
    // Læses ved add-to-cart-tid → altid aktuel view, selv hvis checkbox var afkrydset
    // i privat før skift. Samme check som ex-vat.js isErhverv().
    if (document.documentElement.dataset.customer === 'erhverv') return;
    if (!this.checkbox.checked) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;
    // Undgå at lægge montering oveni montering, hvis hovedvaren ER montering.
    // data-product-id på blokken = montering-PRODUKTETS eget id (ikke sidens produkt),
    // så guarden tripper kun på montering-produktets egen side. product-form.js sender
    // data.productId (dets dataset.productId) i cart:update — aldrig data.variantId.
    if (String(data.productId) === String(this.dataset.productId)) return;

    this.#addMontering(variantId);
  };

  async #addMontering(variantId) {
    this.#adding = true;
    const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const cart = await res.json();
      if (!res.ok || cart.status) {
        // 422 "Kan ikke finde variant" = montering-produktet er ikke publiceret til Webshop-kanalen.
        // Det er en Shopify-publiceringsfejl, IKKE klient-koden — tjek produktets salgskanaler.
        if (res.status === 422) {
          console.warn(
            `[montering-addon] Variant ${variantId} kan ikke lægges i kurv (422). ` +
              'Er montering-produktet publiceret til Webshop-salgskanalen?',
            cart
          );
        } else {
          console.warn('[montering-addon] kunne ikke tilføje montering:', cart);
        }
        return;
      }
      // Bar CartUpdateEvent uden sections → cart-items re-fetcher sig selv (viser montering-linjen).
      // source 'product-form-component' → badge lægger itemCount oveni (+1).
      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id || 'montering-addon', {
          source: 'product-form-component',
          itemCount: 1,
        })
      );
    } catch (err) {
      console.warn('[montering-addon] fetch-fejl ved montering:', err);
    } finally {
      this.#adding = false;
    }
  }
}

if (!customElements.get('montering-addon')) {
  customElements.define('montering-addon', MonteringAddon);
}
